import axios, { AxiosInstance } from 'axios'

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000'

class DataManagerApiClient {
  private client: AxiosInstance

  constructor() {
    this.client = axios.create({
      baseURL: API_BASE_URL,
      timeout: 60000,
    })

    // JWT interceptor (same pattern as omapApi.ts)
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('jwt_token')
        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          localStorage.removeItem('jwt_token')
          window.location.href = 'http://127.0.0.1:5000'
        }
        return Promise.reject(error)
      }
    )
  }

  // =========================================================================
  // Import endpoints
  // =========================================================================

  async getImportTargets(): Promise<{ targets: Array<{ key: string; label: string; domain: string; path: string }> }> {
    const response = await this.client.get('/data/import/targets')
    return response.data.targets
  }

  async getRecordCounts(companyId: number): Promise<Record<string, number>> {
    const response = await this.client.get(`/data/import/counts/${companyId}`)
    return response.data.counts
  }

  async parseFile(companyId: number, file: File): Promise<{
    headers: string[];
    preview_rows: Record<string, unknown>[];
    row_count: number;
  }> {
    const formData = new FormData()
    formData.append('file', file)
    const response = await this.client.post(`/data/import/parse/${companyId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async previewImport(
    companyId: number,
    target: string,
    columnMappings: Record<string, string>,
  ): Promise<{
    valid: boolean;
    mapped_rows: Record<string, unknown>[];
    total_rows: number;
    valid_count: number;
    warning_count: number;
    error_count: number;
    error_details: Array<{ row: number; field: string; error: string }>;
  }> {
    const formData = new FormData()
    formData.append('target', target)
    formData.append('column_mappings', JSON.stringify(columnMappings))
    const response = await this.client.post(`/data/import/preview/${companyId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  async executeImport(
    companyId: number,
    target: string,
    columnMappings: Record<string, string>,
    onDuplicate?: string,
    userId?: number
  ): Promise<{
    target: string;
    imported_count: number;
    skipped_count: number;
    error_count: number;
    error_rows: Array<{ row: number; error: string }>;
    total_rows: number;
  }> {
    const formData = new FormData()
    formData.append('target', target)
    formData.append('column_mappings', JSON.stringify(columnMappings))
    if (onDuplicate) formData.append('on_duplicate', onDuplicate)
    if (userId !== undefined) formData.append('user_id', String(userId))
    const response = await this.client.post(`/data/import/execute/${companyId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    })
    return response.data
  }

  // =========================================================================
  // CRUD endpoints (generic)
  // =========================================================================

  async listRecords(
    domain: string,
    path: string,
    companyId: number,
    params?: Record<string, unknown>
  ): Promise<{ items: Record<string, unknown>[]; total: number }> {
    const url = path ? `/data/${domain}/${path}/${companyId}` : `/data/${domain}/${companyId}`
    const response = await this.client.get(url, { params })
    return response.data
  }

  async getRecord(
    domain: string,
    path: string,
    companyId: number,
    recordId: number
  ): Promise<Record<string, unknown>> {
    const url = path
      ? `/data/${domain}/${path}/${companyId}/${recordId}`
      : `/data/${domain}/${companyId}/${recordId}`
    const response = await this.client.get(url)
    return response.data
  }

  async createRecord(
    domain: string,
    path: string,
    companyId: number,
    data: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    const url = path ? `/data/${domain}/${path}/${companyId}` : `/data/${domain}/${companyId}`
    const response = await this.client.post(url, data)
    return response.data
  }

  async updateRecord(
    domain: string,
    path: string,
    companyId: number,
    recordId: number,
    data: Record<string, unknown>
  ): Promise<Record<string, unknown>> {
    const url = path
      ? `/data/${domain}/${path}/${companyId}/${recordId}`
      : `/data/${domain}/${companyId}/${recordId}`
    const response = await this.client.put(url, data)
    return response.data
  }

  async deleteRecord(
    domain: string,
    path: string,
    companyId: number,
    recordId: number
  ): Promise<{ success: boolean }> {
    const url = path
      ? `/data/${domain}/${path}/${companyId}/${recordId}`
      : `/data/${domain}/${companyId}/${recordId}`
    const response = await this.client.delete(url)
    return response.data
  }

  // =========================================================================
  // Audit log
  // =========================================================================

  async listAuditLog(
    companyId: number,
    params?: Record<string, unknown>
  ): Promise<{ items: Record<string, unknown>[]; total: number }> {
    const response = await this.client.get(`/data/accounting/audit/${companyId}`, { params })
    return response.data
  }
}

export const dataManagerApi = new DataManagerApiClient()
